/**
 * Iter-52.52 — TappableText.
 *
 * Splits an arbitrary passage into word tokens so:
 *   • Each word can be tapped → opens the shared SyllablePopover
 *     (same UX as the Reading page — meaning + pronunciation + example).
 *   • On 🔊 playback, the parent supplies `activeWordIdx` (from
 *     useTts + word-timing) and we visually highlight that word.
 *
 * Word-index MUST match `/tts/with-timestamps` word_spans order, i.e.
 * every whitespace-separated word counts (spaces/newlines don't).
 * Keeps parity with Reading.jsx's Read-to-Me karaoke pipeline.
 */
import { useMemo } from 'react';

// Split preserving whitespace so re-render keeps original spacing.
// Regex captures: (word) | (whitespace) — one is defined per match.
const TOKEN_RE = /(\s+)|([^\s]+)/g;

export default function TappableText({
  text, className = '', style, activeWordIdx = -1,
  onWordTap, testId,
}) {
  const tokens = useMemo(() => {
    if (!text) return [];
    const out = [];
    let m;
    let wordIdx = -1;
    // reset regex state per invocation.
    TOKEN_RE.lastIndex = 0;
    while ((m = TOKEN_RE.exec(text)) !== null) {
      const ws = m[1];
      const w = m[2];
      if (ws != null) {
        // Preserve newlines as <br>; regular whitespace as text.
        if (ws.includes('\n')) {
          const parts = ws.split('\n');
          for (let i = 0; i < parts.length; i++) {
            if (parts[i]) out.push({ kind: 'ws', text: parts[i] });
            if (i < parts.length - 1) out.push({ kind: 'br' });
          }
        } else {
          out.push({ kind: 'ws', text: ws });
        }
      } else if (w != null) {
        wordIdx += 1;
        out.push({ kind: 'word', text: w, idx: wordIdx });
      }
    }
    return out;
  }, [text]);

  if (!text) return null;

  return (
    <span data-testid={testId} className={className} style={style}>
      {tokens.map((t, i) => {
        if (t.kind === 'ws') return <span key={i}>{t.text}</span>;
        if (t.kind === 'br') return <br key={i}/>;
        const isActive = activeWordIdx === t.idx;
        return (
          <span
            key={i}
            role="button"
            tabIndex={0}
            data-word={t.idx}
            onClick={(e) => { e.stopPropagation(); onWordTap && onWordTap(t.text, t.idx, text); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onWordTap && onWordTap(t.text, t.idx, text);
              }
            }}
            className={`cursor-pointer rounded-sm transition-colors ${
              isActive
                ? 'bg-[#FFF3E0] ring-2 ring-[#FF8A3D]/70 rounded-md px-0.5 shadow-[0_0_14px_rgba(255,138,61,0.55)] text-[#8B4500]'
                : 'hover:bg-slate-100 active:bg-slate-200'
            }`}
          >
            {t.text}
          </span>
        );
      })}
    </span>
  );
}
